import { useEffect, useState } from 'react'
import { getOutreachJob, listOutreachJobs, startFindEmails, type OutreachAiInfo, type OutreachJob, type RemoteProspect } from '../../lib/adminClient'
import { useAuth } from '../../lib/auth'
import { usdFine } from '../../lib/format'
import { selectCls } from './ui'

// Look up the business addresses prospects publish themselves, so they can be
// written to by email instead of with one of LinkedIn's few free notes.
// LinkedIn contact details are shown only to a person's connections and
// cannot be read by any tool, so this never looks there.

export function FindEmails({ people, ai, onDone }: { people: RemoteProspect[]; ai: OutreachAiInfo | null; onDone: () => void }): JSX.Element | null {
  const session = useAuth()
  const canWrite = session?.role === 'owner' || session?.role === 'admin'
  const [open, setOpen] = useState(false)
  const [tier, setTier] = useState('A')
  const [limit, setLimit] = useState(50)
  const [useAi, setUseAi] = useState(false)
  const [job, setJob] = useState<OutreachJob | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    void listOutreachJobs().then((r) => r.ok && setJob(r.data.jobs.find((j) => j.kind === 'emails') ?? null))
  }, [])
  useEffect(() => {
    if (job?.status !== 'running') return
    const t = setInterval(async () => {
      const r = await getOutreachJob(job.id)
      if (!r.ok) return
      setJob(r.data.job)
      if (r.data.job.status !== 'running') onDone()
    }, 4000)
    return () => clearInterval(t)
  }, [job, onDone])

  const withEmail = people.filter((p) => p.email && p.emailSource).length
  const withForm = people.filter((p) => !(p.email && p.emailSource) && p.contactFormUrl).length
  const candidates = people.filter((p) => !p.email && !p.doNotSend && p.verified && (!tier || p.tier === tier)).length
  const max = ai?.emailsMax ?? 50

  async function run(): Promise<void> {
    setErr(null)
    const r = await startFindEmails({ tier: tier || null, limit, ...(useAi ? { useAi: true } : {}) })
    if (!r.ok) return setErr(r.error)
    setJob(r.data.job)
  }

  const found = Array.isArray(job?.result?.found) ? (job!.result!.found as Array<{ name: string; email: string }>) : []
  const done = Number(job?.result?.done ?? 0)
  return (
    <div className="glass-card rounded-xl px-4 py-3 mb-4 text-sm" data-testid="find-emails">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-slate-300">
          <span className="font-medium text-white" data-testid="find-emails-count">{withEmail}</span> of {people.length} have a published email
          {withForm ? <> and <span className="font-medium text-white" data-testid="find-forms-count">{withForm}</span> a contact form</> : null}, and are listed first.
        </span>
        {canWrite && (
          <button className="text-accent hover:underline text-sm" onClick={() => setOpen(!open)} data-testid="find-emails-toggle">
            {open ? 'Hide' : 'Find published emails'}
          </button>
        )}
        {job && (
          <span className="text-xs text-slate-400" data-testid="find-emails-job">
            {job.status === 'running' ? `Looking up… ${done} of ${String(job.params.count)} done` : job.status === 'done' ? `Last lookup: found ${found.length} of ${String(job.params.count)}` : `Last lookup failed: ${job.error}`}
            {` · ${usdFine(job.costUsd)}`}
          </span>
        )}
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <div className="text-xs text-slate-400 max-w-3xl">
            LinkedIn shows contact details only to a person’s connections, and no tool can read them. This looks instead for the address each person publishes themselves,
            free: the contact and about pages of their own website (following its own Contact link, reading addresses Cloudflare scrambles, skipping pages the site asks
            robots not to read), then the owner contact in their podcast’s public feed. Where a site has a contact form but no address, the form is saved as the way in.
            An address is kept only if it is written on a page that was actually opened, and that page is saved as its source. Nothing is guessed.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select className={selectCls} value={tier} onChange={(e) => setTier(e.target.value)} data-testid="find-emails-tier">
              <option value="A">Tier A first</option>
              <option value="B">Tier B</option>
              <option value="C">Tier C</option>
              <option value="">Any tier</option>
            </select>
            <label className="text-xs text-slate-400 flex items-center gap-1.5">
              up to
              <input type="number" min={1} max={max} value={limit} onChange={(e) => setLimit(Math.min(max, Math.max(1, Number(e.target.value))))} className="w-16 bg-black/30 border border-white/10 rounded px-2 py-1 text-sm" data-testid="find-emails-limit" />
              people ({candidates} without an address)
            </label>
            <button className="btn-primary" disabled={job?.status === 'running'} onClick={() => void run()} data-testid="find-emails-run">
              {job?.status === 'running' ? 'Looking up…' : useAi ? 'Look up' : 'Look up (free)'}
            </button>
            <label className="text-xs text-slate-400 flex items-center gap-1.5" title="Uses Claude Haiku 5.5 with web search on Plexii's key">
              <input type="checkbox" checked={useAi} disabled={!ai?.available} onChange={(e) => setUseAi(e.target.checked)} data-testid="find-emails-ai" />
              Also ask AI for anyone still missing (about 4 cents a person)
            </label>
            {!ai?.available && <span className="text-xs text-amber-200">The AI step is off: the server has no Anthropic key.</span>}
          </div>
          {err && <div className="text-xs text-red-300" data-testid="find-emails-error">{err}</div>}
          {found.length > 0 && (
            <div className="text-xs text-slate-300" data-testid="find-emails-found">
              Found: {(found as Array<{ name: string; email: string; how?: string }>).map((f) => `${f.name} (${f.email}${f.how === 'site' ? ', from their site' : f.how === 'podcast' ? ', from their podcast feed' : ', by AI'})`).join(', ')}
              {Number(job?.result?.forms) ? ` · ${String(job!.result!.forms)} contact forms saved` : ''}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
